import {getSSRContext, logger as mithrilLogger, type LogContext, type State} from '@bitstillery/mithril'
import m from '@bitstillery/mithril'
import env from '@bitstillery/common/lib/env'
import EventEmitter from 'eventemitter3'
import i18next from 'i18next'
export {i18next}

import Api from '@bitstillery/common/lib/api'
import {Telemetry, type CaptureFields} from '@bitstillery/common/lib/telemetry'
import {Notifier} from '@bitstillery/common/components'
import {Store} from '@bitstillery/common/lib/store'
import {count_i18n_tags, domain_language, normalize_language, supported_languages} from '@bitstillery/common/lib/i18n'
import {current_i18n_package} from '@bitstillery/common/lib/i18n_packages'
import {PlaceholderPackage, type CmsLocaleTranslationBundle} from '@bitstillery/common/api/fact2server'
import {models} from '@bitstillery/common/models'
import type {View} from '@bitstillery/common/types'
import type {StateCommon} from '@bitstillery/common/lib/state'
import type {ResourceStore} from 'i18next'
import {cached} from '@bitstillery/common/lib/api_cache'
import {is_nested_object, present, property_of} from '@bitstillery/common/lib/utils'
import {unchecked} from '@bitstillery/common/lib/unchecked'

/**
 * Placeholder translations per language. Spelled as a type literal rather than the generated interface:
 * only a literal is assignable to i18next's index-signature `Resource`.
 */
type I18nResources = Record<string, {translation: CmsLocaleTranslationBundle['translation']}>

type I18nTWithShortcut = typeof i18next.t & {
    _: (key: string, context?: Record<string, unknown>) => () => string
}

/**
 * Memoized results for the plain `$t('some.key')` shape.
 *
 * `$t` is the single most-called function in the app — every component calls it, in every `view()`, on
 * every redraw — and i18next does real work per call: it resolves the language hierarchy, walks the
 * resource store (`getResource`), and rebuilds a regex cache key (`looksLikeObjectPath` allocates an
 * array and a string each time). Together those were ~12% of JS self time in a portal render profile.
 *
 * Correctness rests on two things:
 *
 * 1. CLIENT ONLY. On the server this is bypassed entirely. The SSR process is long-lived and serves
 *    every visitor, and `load_i18n_resources` is keyed by (language, PATH) — so a process-wide cache
 *    could serve one request's copy to another. The redraw hot path is client-side anyway; SSR renders
 *    a page once.
 * 2. Cleared whenever resources are (re)installed. Translations enter ONLY through `init_language`,
 *    which re-runs per route because CMS copy is per-path, so clearing there covers every way the
 *    answer can change. The language is also part of the key, as a second line of defence.
 *
 * Calls WITH options (interpolation, count, context, defaultValue) are not cached: building a key for
 * them costs about as much as the lookup, and getting it subtly wrong would return the wrong string.
 */
const translation_cache = new Map<string, string>()

/** Drop memoized translations; call whenever the resource bundle changes. */
export function clear_translation_cache() {
    translation_cache.clear()
}

let translation_cache_hooked = false

/**
 * Invalidate the memo from i18next's own events rather than from call sites.
 *
 * Resources do not only arrive via `init_language`: `set_language` and `load_placeholders`
 * (common/lib/i18n.ts) call `addResourceBundle` directly, and `load_placeholders` merges NEW keys for the
 * SAME language — so a key that previously resolved to itself (missing) would otherwise stay cached as
 * missing after the bundle that defines it arrives. Hooking the store catches every path, including ones
 * added later.
 *
 * Registration is guarded: `init()` runs once per SSR request, and re-registering per request would leak
 * listeners exactly the way the hierarchy memo did.
 */
function install_translation_cache_invalidation() {
    if (translation_cache_hooked) {
        return
    }
    // i18next declares its store as always there, but only its `init` creates one.
    const emitter: {on?: (event: string, cb: () => void) => void; store?: ResourceStore} = i18next
    const store = emitter.store
    if (typeof emitter.on !== 'function' || typeof store?.on !== 'function') {
        logger.debug('[app] i18next emitter/store missing — translation memo will rely on init_language only')
        return
    }
    translation_cache_hooked = true
    emitter.on('languageChanged', clear_translation_cache)
    store.on('added', clear_translation_cache)
    store.on('removed', clear_translation_cache)
}

function memoized_t(key: unknown, options?: unknown): unknown {
    if (options !== undefined || typeof key !== 'string' || globalThis.__SSR_MODE__) {
        return Reflect.apply(i18next.t, i18next, [key, options])
    }
    const cache_key = `${i18next.language}|${key}`
    let value = translation_cache.get(cache_key)
    if (value === undefined) {
        value = (i18next.t as (key: string) => string)(key)
        translation_cache.set(cache_key, value)
    }
    return value
}

const t_with_shortcut: I18nTWithShortcut = Object.assign(
    // It hands every call on to i18next.t, whose overloads a wrapper can't restate.
    unchecked<typeof i18next.t>(memoized_t),
    {
        _: (key: string, context?: Record<string, unknown>) => () =>
            context === undefined ? i18next.t(key) : i18next.t(key, context),
    },
)

/**
 * Portal overlay routes (auth / invite) are dialogs rendered over the home page, so the
 * CMS content + i18n behind them come from '/', not the literal URL. (Design debt: this encodes
 * portal route paths in common; revisit once routes are shared across packages.)
 */
const OVERLAY_ROUTES = new Set(['/login', '/register', '/forgot-password', '/reset-password'])

export function cms_content_path(uri: string): string {
    return OVERLAY_ROUTES.has(uri) || uri.startsWith('/invite/') ? '/' : uri
}

/**
 * The settings every app's bootstrap hands `App.init`. Settings only one app has (the portal's tawk id,
 * discover's portal links) stay in that app's own `lib/config`, which is where its code reads them.
 */
export interface Config {
    api_host: string
    api_host_new: string
    env: string
    media_photo_host: string
    product_photo_host: string
    sentry_dist: string | undefined
    sentry_dsn: string | undefined
}

// `App.init` copies the app's config over these; nothing reads them before that.
export const config: Config = {
    api_host: '',
    api_host_new: '',
    env: '',
    media_photo_host: '',
    product_photo_host: '',
    sentry_dist: undefined,
    sentry_dsn: undefined,
}
export const $m = models

export const api = new Api()
// Client singleton EventEmitter; during SSR, getSSRContext().events is the request-scoped EventEmitter
const client_events = new EventEmitter()
export const notifier = new Notifier()

// Initialize telemetry first (needed for logger.exception)
export const telemetry = new Telemetry()

// Re-export mithril logger with compatibility methods
// Explicitly copy methods since class instance methods aren't enumerable
const logger = {
    info: mithrilLogger.info.bind(mithrilLogger),
    debug: mithrilLogger.debug.bind(mithrilLogger),
    warn: mithrilLogger.warn.bind(mithrilLogger),
    error: mithrilLogger.error.bind(mithrilLogger),
    setPrefix: mithrilLogger.setPrefix.bind(mithrilLogger),
    // Compatibility: set_level for old logger API
    set_level(_level: 'debug' | 'info' | 'warn' | 'error'): void {
        // Mithril logger doesn't have log level filtering, so this is a no-op
        // The mithril logger already handles debug filtering via __SSR_MODE__ and NODE_ENV
    },
    // Compatibility: exception method that sends to telemetry
    exception(message: string, context?: LogContext & CaptureFields): void {
        if (telemetry.enabled) {
            telemetry.capture_message(message, context)
        }
        mithrilLogger.error(message, undefined, context)
    },
}
export {logger}

// Client singleton; during SSR, getSSRContext().store is the request-scoped store
const client_store = new Store<StateCommon>()

/**
 * Returns the current EventEmitter: request-scoped EventEmitter during SSR (from AsyncLocalStorage),
 * otherwise the client singleton. Components import events and get the right instance.
 */
function get_events(): EventEmitter {
    const ctx = getSSRContext()
    if (ctx?.events) {
        // The fork leaves the context's emitter untyped; portal/src/server.ts puts an eventemitter3 one there.
        return unchecked<EventEmitter>(ctx.events)
    }
    return client_events
}

/**
 * An object that reads every member off whatever `current()` returns at the time, bound to it, so a
 * module-level export follows the SSR request it's used in.
 */
function forward<T extends object>(current: () => T): T {
    // The target is never read; every read goes through `get`.
    return new Proxy(unchecked<T>({}), {
        get(_, prop): unknown {
            const target = current()
            const value: unknown = Reflect.get(target, prop)
            return typeof value === 'function' ? value.bind(target) : value
        },
    })
}

// Proxy so "events" always resolves to the current request's EventEmitter (SSR) or client EventEmitter
export const events = forward(get_events)

/**
 * Returns the current store: request-scoped store during SSR (from AsyncLocalStorage),
 * otherwise the client singleton. Components import store / $s and get the right instance.
 */
export function get_store(): Store<StateCommon> {
    const ctx = getSSRContext()
    if (ctx?.store) {
        // The SSR context is app-agnostic; the store in it is the one server.ts built for this app's state.
        return unchecked<Store<StateCommon>>(ctx.store)
    }
    return client_store
}

// Proxy so "store" always resolves to the current request's store (SSR) or client store
export const store = forward(get_store)

// Proxy so "$s" always resolves to the current store's state (SSR or client)
export const $s = new Proxy(unchecked<State<StateCommon>>({}), {
    get(_, prop): unknown {
        return Reflect.get(get_store().state, prop)
    },
    has(_, prop) {
        return prop in get_store().state
    },
    set(_, prop, value) {
        Reflect.set(get_store().state, prop, value)
        return true
    },
})
export const $t = t_with_shortcut
export const view: View = {
    collection: null,
}

/** The lifecycle hooks `App.init` calls on a model that has them. */
export interface AppModelHooks {
    bind?: () => void
    init?: () => void
}

export type AppModel<T extends object = object> = T & AppModelHooks

/**
 * Placeholder i18n resources per (language, cms-content-path) — the same for every visitor, so it is
 * cached process-wide and reused across SSR requests instead of refetched each render. Short TTL keeps
 * published placeholder edits fresh; call the cache-clear endpoint (`portal/cms/i18n`) to drop it
 * immediately after a Discover placeholder publish. `handoff` is off: `init_language` keeps its own
 * `__SSR_STATE__.__meta.i18n` client handoff.
 */
const load_i18n_resources = cached(
    'portal/cms/i18n',
    async (
        language_code: string,
        path: string | undefined,
        pkg: string,
    ): Promise<Record<string, CmsLocaleTranslationBundle> | null> => {
        const params: {language_code: string; package: string; path?: string} = {language_code, package: pkg}
        if (path) params.path = path
        const {result, success} = await api.get('portal/cms/i18n', params)
        if (!success) {
            notifier.notify('Cannot load the translations', 'warning')
            return null
        }
        return result.resources
    },
    {
        ttl: 60_000,
        scope: 'public',
        handoff: false,
        key: (language_code, path, pkg) => `${language_code}|${path ?? ''}|${pkg}`,
        // A failed load must not stick for the TTL; the next init retries it.
        store_if: (resources) => resources !== null,
    },
)

export class App {
    init(app_config: Config, app_models: Record<string, object>) {
        Object.assign(config, app_config)
        Object.assign($m, app_models)
        notifier.load($s.notifications)
        env($s.env)

        // Set logger prefix to [app] for application code (works in both SSR and client)
        logger.setPrefix('[app]')

        telemetry.initialize_sentry()

        const version = process.env.MSI_PACKAGE === 'discover' ? process.env.MSI_SCHEME_DISCOVER : process.env.MSI_SCHEME_PORTAL
        if ($s.scheme.version !== version) {
            store.set('store', {identity: $s.identity, scheme: {changed: true, version}})
            window.location.reload()
            return
        }

        if (process.env.BUN_ENV === 'development' || $s.settings.beta) {
            logger.set_level('debug')
        } else {
            logger.set_level('info')
        }

        // Beta doubles as the debug surface shared with tooling: `bun cli profile` sets the
        // `store_prefs` cookie to enable it, then drives the app through these handles. `m` is here for
        // that — it carries `redraw`, which is what a render profile needs in order to time the vdom
        // diff on its own, with no event, network or paint mixed in.
        if ($s.settings.beta) {
            Object.assign(globalThis, {$m, $s, app: this, m, notifier, route: m.route})
        }

        // Reload was done;
        if ($s.scheme.changed) {
            notifier.notify($t('system.scheme_change'), 'warning', 10000, 'info')
            $s.scheme.changed = false
            void store.save()
        }

        for (const model of Object.values<AppModelHooks>(app_models)) {
            if (model.bind) {
                model.bind()
            }
        }

        for (const model of Object.values<AppModelHooks>(app_models)) {
            if (model.init) {
                model.init()
            }
        }
    }

    async init_language(i18n_resources?: I18nResources) {
        let hostname = ''
        if (typeof document !== 'undefined') {
            if (window.location === window.parent.location) {
                // Is code running in iframe?
                ;({hostname} = window.location)
            } else {
                try {
                    ;({hostname} = new URL(document.referrer)) // Iframe, take url from parent
                } catch {
                    // Ignore
                }
            }
        } else {
            // No document during SSR; the server puts the request's host on the render context so the
            // domain resolves to the same language server- and client-side (see portal/src/server.ts).
            const ssr_hostname = property_of(getSSRContext(), 'ssr_hostname')
            hostname = typeof ssr_hostname === 'string' ? ssr_hostname : ''
        }

        const supported_language_codes = new Set(supported_languages.map((option) => option[0]))
        const profile_language = $s.identity.user.language
        // Empty until someone (a visitor switching, or a previous init) persisted a choice; the domain
        // decides for everyone else, so an unset default must not read as a preference here.
        const selected_language = $s.language
        // Bun's SSR runtime has a `navigator` without a `language`, so check the value, not the global.
        const browser_language =
            typeof navigator !== 'undefined' && typeof navigator.language === 'string' ? navigator.language : ''
        const navigator_language = browser_language ? normalize_language(browser_language, supported_language_codes) : null
        const tld_language = domain_language(hostname)

        const url_params = m.parsePathname(m.route.get())
        const url_language: unknown = url_params.params.language
        if (typeof url_language === 'string' && supported_language_codes.has(url_language)) {
            $s.language = url_language
        } else if (profile_language && supported_language_codes.has(profile_language)) {
            $s.language = profile_language
        } else if (selected_language && supported_language_codes.has(selected_language)) {
            $s.language = selected_language
        } else if (tld_language && supported_language_codes.has(tld_language)) {
            // Above the browser's own preference on purpose: picking movingspirits.de over .nl is a
            // choice of storefront, and the storefront's language is the one its copy is written in.
            $s.language = tld_language
        } else if (navigator_language) {
            $s.language = navigator_language
        } else {
            $s.language = 'en-GB'
        }

        logger.info(`init language: ${$s.language}`)

        // Both apps now load i18n from placeholders. Discover fetches its own package's keys (plus the
        // shared `common` ones) and has no CMS pages, so it passes no path; portal loads the current page's
        // copy, mapping overlay routes (/login, …, which render the home page behind a dialog) through
        // `cms_content_path` rather than the literal overlay URL.
        const i18n_package = current_i18n_package()
        const i18n_path = i18n_package === PlaceholderPackage.Discover ? undefined : cms_content_path($s.env.uri)
        if (!i18n_resources) {
            // First browser init reuses the i18n the server already loaded, carried raw in
            // `__SSR_STATE__.__meta.i18n` — no reactive state, no extra round-trip. Only the portal's
            // Window declares that state.
            const ssr_i18n = globalThis.__SSR_MODE__
                ? undefined
                : property_of(property_of(property_of(window, '__SSR_STATE__'), '__meta'), 'i18n')
            const ssr_resources = property_of(ssr_i18n, 'resources')
            if (property_of(ssr_i18n, 'language') === $s.language && is_nested_object(ssr_resources)) {
                // What the server's own load below stashed there.
                i18n_resources = unchecked<I18nResources>(ssr_resources)
            } else {
                const resources = await load_i18n_resources($s.language, i18n_path, i18n_package)
                if (!resources) return
                i18n_resources = resources
                // On the server, stash it raw in the SSR state so the browser can reuse it (above).
                if (globalThis.__SSR_MODE__) {
                    const ctx = getSSRContext()
                    if (ctx) {
                        ctx.ssrStateMeta = {...(ctx.ssrStateMeta ?? {}), i18n: {language: $s.language, resources}}
                    }
                }
            }
        }

        const tag_count = count_i18n_tags(present(i18n_resources[$s.language], `i18n resources for ${$s.language}`).translation)
        logger.debug(`[app] loaded i18n (${$s.language}) from placeholders (${tag_count} translations)`)

        await i18next.init({
            // Do not debug for missing i18n keys in ssr mode, and only on the client during development
            debug: process.env.MSI_PACKAGE === 'portal' && !globalThis.__SSR_MODE__ && process.env.BUN_ENV === 'development',
            fallbackLng: 'en-GB',
            lng: $s.language,
            ns: ['translation', 'cms'],
            defaultNS: 'translation',
            initAsync: false,
            resources: i18n_resources,
        })

        memoize_language_hierarchy()
        // Resources have just been (re)installed — possibly a different language, and on the portal a
        // different per-path CMS bundle — so anything memoized against the previous bundle is now stale.
        install_translation_cache_invalidation()
        clear_translation_cache()
    }
}

/**
 * Cache i18next's language-code hierarchy, which it otherwise recomputes on EVERY `t()` call.
 *
 * `Translator.resolve` calls `languageUtils.toResolveHierarchy(lng, fallbackLng)` per lookup, and that
 * runs `formatLanguageCode` for the code and each fallback — each of which calls
 * `Intl.getCanonicalLocales()`, because our codes contain a '-' ('en-GB'). i18next already caches its
 * `PluralRules` and formatters, but not this. On the portal landing page it showed up as ~40ms of
 * `formatLanguageCode` per 60 redraws (`bun cli profile portal / --scenario redraw`), since every `$t()`
 * in every `view()` body re-runs on every redraw.
 *
 * WHY THIS IS SAFE ACROSS A LANGUAGE CHANGE — and why memoizing `$t` itself would NOT be: the active
 * language is an ARGUMENT here (`opt.lng || this.language`), not hidden state read inside the function.
 * Switching language changes `code`, which changes the cache key, so a switch simply misses the cache and
 * recomputes. Memoizing `$t` output would instead key on the translation key and silently serve the old
 * language's string — as well as breaking interpolation params, plurals/context, and CMS placeholder
 * updates. So the memo goes on the pure code→codes normalisation, not on translation.
 *
 * The other inputs (`fallbackLng`, `load`, `supportedLngs`) are fixed at `init` and never mutated, and
 * the key space is the handful of language codes we ship. Resources loaded later do not affect this — it
 * derives language codes only, never translations.
 */
const HIERARCHY_MEMO_FLAG = '__discover_hierarchy_memoized__'

/** The piece of i18next's internal LanguageUtils the memo wraps; internal, so any of it may be missing. */
interface LanguageUtilsInternals {
    [HIERARCHY_MEMO_FLAG]?: boolean
    toResolveHierarchy?: (code: string, fallback_code?: unknown) => string[]
}

function memoize_language_hierarchy() {
    const internals: {services?: {languageUtils?: LanguageUtilsInternals}} = i18next
    const language_utils = internals.services?.languageUtils
    // Defensive: this reaches into i18next internals, so a version bump that moves them must degrade to
    // the current (correct, slower) behaviour rather than throwing at boot.
    if (!language_utils || typeof language_utils.toResolveHierarchy !== 'function') {
        logger.debug('[app] i18next languageUtils.toResolveHierarchy missing — skipping hierarchy memo')
        return
    }

    // MUST be idempotent. `init()` runs once per SSR REQUEST (portal/src/server.ts `initRequestContext`)
    // while i18next is a module singleton, so without this guard every request wraps the previous
    // wrapper. The closures nest, each `t()` call walks the whole chain, and the SSR process degrades
    // request by request until it stops responding — which is exactly what happened when this landed
    // without the flag.
    if (language_utils[HIERARCHY_MEMO_FLAG]) {
        return
    }
    language_utils[HIERARCHY_MEMO_FLAG] = true

    const cache = new Map<string, string[]>()
    const uncached = language_utils.toResolveHierarchy.bind(language_utils)
    language_utils.toResolveHierarchy = (code: string, fallback_code?: unknown): string[] => {
        const key = `${code}|${fallback_code === undefined ? '' : JSON.stringify(fallback_code)}`
        let codes = cache.get(key)
        if (!codes) {
            codes = uncached(code, fallback_code)
            cache.set(key, codes)
        }
        // Hand out a copy: callers treat the result as their own array, and one i18next branch pushes
        // onto it. Sharing the cached instance would let a caller corrupt every later lookup.
        return codes.slice()
    }
}
